"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import {
  Sun,
  Moon,
  Phone,
  Menu,
  X,
  ChevronDown,
  ArrowUpRight,
  Sparkles,
  Calendar,
  Layers,
  ChevronRight,
} from "lucide-react";
import { MAIN_NAV_ITEMS, USLUGI_MEGA_MENU, EVENT_DROPDOWN_ITEMS } from "./navigation/nav-data";
import { MegaMenu } from "./navigation/MegaMenu";
import { EventDropdown } from "./navigation/EventDropdown";

interface HeaderProps {
  onOpenOrderModal: (service?: string) => void;
}

export function Header({ onOpenOrderModal }: HeaderProps) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<"uslugi" | "event" | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileUslugiOpen, setMobileUslugiOpen] = useState(false);
  const [mobileEventOpen, setMobileEventOpen] = useState(false);
  const [expandedMobileBlock, setExpandedMobileBlock] = useState<string | null>(null);

  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      setScrolled(window.scrollY > 15);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setActiveMenu(null);
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = (type: "uslugi" | "event") => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveMenu(type);
  };

  const handleMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 180);
  };

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  const handleNavLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    setActiveMenu(null);
    setMobileMenuOpen(false);

    const sectionMap: Record<string, string> = {
      "/": "hero",
      "/portfolio/": "portfolio",
      "/portfolio": "portfolio",
      "/o-kompanii/": "about",
      "/o-kompanii": "about",
      "/kak-rabotaem/": "order",
      "/kak-rabotaem": "order",
      "/kontakty/": "contacts",
      "/kontakty": "contacts",
      "/uslugi/": "services",
      "/uslugi": "services",
      "/event/": "order",
      "/event": "order",
    };

    const sectionId = sectionMap[href];
    if (sectionId && typeof window !== "undefined" && window.location.pathname === "/") {
      const el = document.getElementById(sectionId);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, "", href);
      }
    }
  };

  const isGlass = scrolled || activeMenu !== null || mobileMenuOpen;

  return (
    <header
      ref={headerRef}
      onMouseLeave={handleMouseLeave}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isGlass
        ? "bg-white/75 dark:bg-[#0B0D14]/80 backdrop-blur-xl border-b border-slate-200/60 dark:border-slate-800/60 shadow-[0_4px_24px_rgba(0,0,0,0.04)] dark:shadow-[0_4px_30px_rgba(0,0,0,0.4)] py-3"
        : "bg-transparent border-b border-transparent py-4 sm:py-5"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between gap-4">
          <Link
            href="/"
            onClick={(e) => handleNavLinkClick(e, "/")}
            className="flex items-center gap-2.5 sm:gap-3 group cursor-pointer select-none shrink-0"
            id="header-logo"
          >
            <div className="relative w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
              <Image
                src="/images/logo-icon.png"
                alt="Альянс-Дизайн"
                fill
                sizes="36px"
                className="object-contain"
                priority
              />
            </div>

          </Link>

          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            {MAIN_NAV_ITEMS.map((item) => {
              if (item.type === "megamenu-uslugi") {
                const isOpen = activeMenu === "uslugi";
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter("uslugi")}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveMenu(isOpen ? null : "uslugi")}
                      className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 select-none ${isOpen
                        ? "text-[#0a3d90] dark:text-[#55a9ef] bg-gray-100/90 dark:bg-zinc-800/90 font-semibold"
                        : "text-gray-700 dark:text-zinc-200 hover:text-[#0a3d90] dark:hover:text-[#55a9ef] hover:bg-gray-100/60 dark:hover:bg-zinc-800/50"
                        }`}
                      aria-expanded={isOpen}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 dark:text-zinc-400 transition-transform duration-200 ${isOpen ? "rotate-180 text-[#0a3d90] dark:text-[#55a9ef]" : ""
                          }`}
                      />
                    </button>
                  </div>
                );
              }

              if (item.type === "dropdown-event") {
                const isOpen = activeMenu === "event";
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter("event")}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveMenu(isOpen ? null : "event")}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 select-none ${isOpen
                        ? "text-[#0a3d90] dark:text-[#55a9ef] bg-gray-100/90 dark:bg-zinc-800/90 font-semibold"
                        : "text-gray-700 dark:text-zinc-200 hover:text-[#0a3d90] dark:hover:text-[#55a9ef] hover:bg-gray-100/60 dark:hover:bg-zinc-800/50"
                        }`}
                      aria-expanded={isOpen}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 dark:text-zinc-400 transition-transform duration-200 ${isOpen ? "rotate-180 text-[#0a3d90] dark:text-[#55a9ef]" : ""
                          }`}
                      />
                    </button>
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavLinkClick(e, item.href)}
                  onMouseEnter={() => setActiveMenu(null)}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-zinc-200 hover:text-[#0a3d90] dark:hover:text-[#55a9ef] hover:bg-gray-100/60 dark:hover:bg-zinc-800/50 transition-all duration-150"
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden sm:flex items-center gap-3 xl:gap-4 shrink-0">
            <a
              href="tel:88001234567"
              className="hidden lg:flex items-center gap-2 text-xs xl:text-sm font-semibold tracking-tight text-gray-900 dark:text-gray-100 hover:text-[#0a3d90] dark:hover:text-[#55a9ef] transition-colors"
              id="header-phone"
            >
              <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-zinc-800 flex items-center justify-center text-gray-700 dark:text-gray-300">
                <Phone className="w-3.5 h-3.5" />
              </div>
              <span className="hidden xl:inline">8 800 123-45-67</span>
            </a>

            <button
              onClick={toggleTheme}
              className="hidden lg:flex p-2 rounded-lg border border-gray-300 dark:border-zinc-700 bg-white/90 dark:bg-zinc-900/90 text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-zinc-500 transition-colors"
              aria-label="Переключить тему"
              id="theme-toggle-button"
            >
              {mounted && resolvedTheme === "dark" ? (
                <Sun className="w-4 h-4 text-amber-400 transition-transform duration-300 hover:rotate-45" />
              ) : (
                <Moon className="w-4 h-4 text-indigo-600 transition-transform duration-300 hover:-rotate-12" />
              )}
            </button>
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full border border-gray-300 dark:border-zinc-700 bg-white/90 dark:bg-zinc-900/90 text-gray-700 dark:text-gray-200"
              aria-label="Переключить тему"
            >
              {mounted && resolvedTheme === "dark" ? (
                <Sun className="w-4 h-4 text-amber-400" />
              ) : (
                <Moon className="w-4 h-4 text-indigo-600" />
              )}
            </button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-zinc-800"
              aria-label="Меню"
              id="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <MegaMenu
          isOpen={activeMenu === "uslugi"}
          onClose={() => setActiveMenu(null)}
          onOpenOrderModal={onOpenOrderModal}
        />

        <EventDropdown
          isOpen={activeMenu === "event"}
          onClose={() => setActiveMenu(null)}
          onOpenOrderModal={onOpenOrderModal}
        />
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden max-h-[85vh] overflow-y-auto bg-[#F8F9FA] dark:bg-[#0B0D14] border-b border-gray-200 dark:border-zinc-800 px-4 sm:px-6 py-6 space-y-4">
          <nav className="flex flex-col space-y-2">

            <div className="border border-gray-200/80 dark:border-zinc-800/80 rounded-xl overflow-hidden bg-white/60 dark:bg-zinc-900/40">
              <button
                type="button"
                onClick={() => setMobileUslugiOpen(!mobileUslugiOpen)}
                className="w-full flex items-center justify-between p-3.5 text-base font-bold text-gray-950 dark:text-white"
              >
                <span>Услуги и продукция</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${mobileUslugiOpen ? "rotate-180 text-[#0a3d90]" : ""
                    }`}
                />
              </button>

              {mobileUslugiOpen && (
                <div className="p-3 border-t border-gray-100 dark:border-zinc-800/80 space-y-3">
                  {USLUGI_MEGA_MENU.map((block) => {
                    const isExpanded = expandedMobileBlock === block.id;
                    return (
                      <div
                        key={block.id}
                        className="rounded-lg border border-gray-100 dark:border-zinc-800/60 p-2.5 bg-gray-50/50 dark:bg-zinc-900/60"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedMobileBlock(isExpanded ? null : block.id)
                          }
                          className="w-full flex items-center justify-between text-xs font-bold text-gray-900 dark:text-zinc-100 py-1"
                        >
                          <span className="flex text-base items-center gap-1.5">
                            
                            {block.title}
                          </span>
                          <ChevronRight
                            className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-90 text-[#0a3d90]" : ""
                              }`}
                          />
                        </button>

                        {isExpanded && (
                          <div className="pt-2 pl-3 space-y-1.5 border-t border-gray-100 dark:border-zinc-800/60 mt-1.5">
                 
                            {block.directItems?.map((item) => (
                              <Link
                                key={item.href}
                                href={item.href}
                                onClick={(e) => handleNavLinkClick(e, item.href)}
                                className="block py-1 text-base text-gray-600 dark:text-zinc-300 hover:text-[#0a3d90]"
                              >
                                {item.title}
                              </Link>
                            ))}

                            {block.subGroups?.map((sub) => (
                              <div key={sub.title} className="pt-5">
                                <span className="block text-[11px] pb-2 font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider">
                                  {sub.title}
                                </span>
                                {sub.items.map((item) => (
                                  <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={(e) => handleNavLinkClick(e, item.href)}
                                    className="block py-1 text-base text-gray-600 dark:text-zinc-300 hover:text-[#0a3d90]"
                                  >
                                    {item.title}
                                  </Link>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Link
                    href="/uslugi/"
                    onClick={(e) => handleNavLinkClick(e, "/uslugi/")}
                    className="block text-center py-2 text-base font-semibold text-[#0a3d90] dark:text-[#55a9ef] bg-blue-50/50 dark:bg-blue-950/20 rounded-lg"
                  >
                    Смотреть все услуги каталога
                  </Link>
                </div>
              )}
            </div>

            <div className="border border-pink-200/60 dark:border-pink-900/40 rounded-xl overflow-hidden bg-pink-50/20 dark:bg-pink-950/10">
              <button
                type="button"
                onClick={() => setMobileEventOpen(!mobileEventOpen)}
                className="w-full flex items-center justify-between p-3.5 text-base font-bold text-gray-950 dark:text-white"
              >
                <div className="flex items-center gap-2">
                  <span>Оформление мероприятий</span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-pink-500/15 text-[#FF2E93] font-bold">
                    Новое
                  </span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${mobileEventOpen ? "rotate-180 text-[#FF2E93]" : ""
                    }`}
                />
              </button>

              {mobileEventOpen && (
                <div className="p-3 pt-0 border-t border-pink-100 dark:border-pink-900/30 space-y-1">
                  {EVENT_DROPDOWN_ITEMS.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={(e) => handleNavLinkClick(e, item.href)}
                      className="block py-1.5 px-2 rounded text-xs font-medium text-gray-700 dark:text-zinc-300 hover:text-[#FF2E93]"
                    >
                      • {item.title}
                    </Link>
                  ))}

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        onOpenOrderModal("Оформление мероприятий");
                      }}
                      className="w-full py-2 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-[#0a3d90] to-[#55a9ef] text-center"
                    >
                      Индивидуальный расчет сметы
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Link
              href="/portfolio/"
              onClick={(e) => handleNavLinkClick(e, "/portfolio/")}
              className="text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-[#0a3d90] py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800/60 flex items-center justify-between"
            >
              <span>Портфолио</span>
              <ArrowUpRight className="w-4 h-4 opacity-60" />
            </Link>

            <Link
              href="/o-kompanii/"
              onClick={(e) => handleNavLinkClick(e, "/o-kompanii/")}
              className="text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-[#0a3d90] py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800/60 flex items-center justify-between"
            >
              <span>О компании</span>
              <ArrowUpRight className="w-4 h-4 opacity-60" />
            </Link>

            <Link
              href="/kak-rabotaem/"
              onClick={(e) => handleNavLinkClick(e, "/kak-rabotaem/")}
              className="text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-[#0a3d90] py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800/60 flex items-center justify-between"
            >
              <span>Как заказать</span>
              <ArrowUpRight className="w-4 h-4 opacity-60" />
            </Link>

            <Link
              href="/kontakty/"
              onClick={(e) => handleNavLinkClick(e, "/kontakty/")}
              className="text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-[#0a3d90] py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800/60 flex items-center justify-between"
            >
              <span>Контакты</span>
              <ArrowUpRight className="w-4 h-4 opacity-60" />
            </Link>
          </nav>

          <div className="pt-4 border-t border-gray-200 dark:border-zinc-800 flex flex-col gap-3">
            <a
              href="tel:88001234567"
              className="flex items-center gap-2.5 text-sm font-bold text-gray-900 dark:text-gray-100"
            >
              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-zinc-800 flex items-center justify-center text-gray-700 dark:text-gray-300">
                <Phone className="w-4 h-4" />
              </div>
              <span>8 800 123-45-67</span>
            </a>

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenOrderModal();
              }}
              className="w-full py-2.5 rounded-lg text-sm font-medium bg-[#0a3d90] dark:bg-[#3b82f6] text-white hover:bg-[#083070] dark:hover:bg-[#2563eb] text-center transition-colors"
            >
              Оставить заявку
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
